import { Table, DatePicker, Select, Button, Modal, Input, Timeline, Tag, Typography } from "antd";
import { useState, useContext, useEffect, useRef } from "react";
import dayjs from "dayjs";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { CloseOutlined } from "@ant-design/icons";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

const { Text } = Typography;
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export default function LeaveTable({ data, targetLeaveId }) {
  const [filters, setFilters] = useState({
    leaveType: null,
    status: null,
    month: null,
    year: null,
    fromDate: null,
    toDate: null,
  });

  const { authData, backUrl } = useContext(AuthContext);
  const [leaves, setLeaves] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [leaveCancelReason, setLeaveCancelReason] = useState("");
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Ref for scrolling to the highlighted row
  const highlightRowRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ Fetch leave data
  const fetchData = async () => {
    try {
      const res = await axios.get(`${backUrl}/api/hr/leaves/my-leaves/`, {
        headers: { Authorization: `Bearer ${authData.access}` },
      });
      setLeaves(res.data.results || []);
    } catch (error) {
      console.error("Error fetching leaves:", error.response || error.message);
    }
  };

  // ✅ Initial data load
  useEffect(() => {
    if (data === null) {
      fetchData();
    } else {
      setLeaves(data);
    }
  }, [data]);

  // ✅ Scroll to and highlight the target leave after data loads
  useEffect(() => {
    if (targetLeaveId && leaves.length > 0) {
      const targetIdNum = parseInt(targetLeaveId, 10);
      const found = leaves.find((l) => l.id === targetIdNum);
      if (found) {
        console.log("🎯 LeaveTable: Found target leave:", found);
        setTimeout(() => {
          if (highlightRowRef.current) {
            highlightRowRef.current.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 300);
      }
    }
  }, [targetLeaveId, leaves]);

  // ✅ Status colors
  const statusColors = {
    "Pending Approval": "orange",
    Approved: "green",
    Rejected: "red",
    Cancelled: "gray",
    "Pending Cancellation": "volcano",
  };

  // ✅ Columns
  const columns = [
    {
      title: "Leave Type",
      dataIndex: "leave_type",
      key: "leave_type",
      width: 180,
      render: (text) => (
        <p>{text === "Lop" ? "NLB (Nil Leave Balance)" : text}</p>
      ),
    },
    {
      title: "From",
      dataIndex: "start_date",
      key: "start_date",
      render: (date) => dayjs(date).format("DD/MM/YYYY"),
    },
    {
      title: "To",
      dataIndex: "end_date",
      key: "end_date",
      render: (date) => dayjs(date).format("DD/MM/YYYY"),
    },
    {
      title: "Duration", dataIndex: "duration_type", key: "duration_type",
      render: (text, record) => (
        <>
          <p>{text}</p>
          <p>{record.session}</p>
        </>
      )
    },
    { title: "Days", dataIndex: "leave_days_count", key: "leave_days_count" },
    {
      title: "Reason",
      key: "reason",
      width: 400,
      render: (_, record) => (
        <>
          <div
            style={{
              whiteSpace: "normal",
              wordWrap: "break-word",
              maxWidth: "400px",
            }}
          >
            {record.reason}
          </div>
          <br />
          <br />
          {record.cancellation_reason && (
            <div
              style={{
                marginTop: "4px",
                fontWeight: 600,
                color: "red",
                backgroundColor: "#ffe6e6",
                padding: "3px 6px",
                borderRadius: "6px",
                display: "inline-block",
              }}
            >
              Cancellation Reason: {record.cancellation_reason}
            </div>
          )}

          {
            (record.work_dates && record?.work_dates.length > 0) && (
              <>
                <p style={{ fontWeight: 'bold' }}>Worked Date : <span style={{ fontWeight: 'normal' }}>{record?.work_dates?.map((date, index) => date)}</span></p>

              </>
            )
          }
        </>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text) => (
        <span
          style={{
            fontWeight: 500,
            color:
              text === "Approved"
                ? "green"
                : text === "Rejected"
                  ? "red"
                  : "#faad14", // Pending or others
          }}
        >
          {text}
        </span>
      ),
    },

    {
      title: "Approval Hierarchy",
      key: "remarks",
      width: 450,
      render: (_, record) => {
        const approvals = record.approvals || [];
        if (!approvals.length) return "-";

        return (
          <div style={{ marginTop: "16px" }}>
            <Timeline
              items={approvals.map((a, index) => {
                const isRejected = !!(a.rejected_on || a.cancellation_rejected_on);
                const isApproved = !!(a.approved_on || a.cancellation_approved_on);
                const timelineColor = isRejected ? "red" : isApproved ? "green" : "gray";

                return {
                  color: timelineColor,
                  children: (
                    <div style={{ marginBottom: "8px" }}>
                      {/* Name and Status Tag */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px" }}>
                        <Text strong style={{ fontSize: "15px" }}>
                          {a.approver_name || 
                           (a.approver?.first_name || a.approver?.last_name 
                            ? `${a.approver.first_name || ""} ${a.approver.last_name || ""}` 
                            : (a.approver?.user?.first_name || a.approver?.user?.last_name 
                               ? `${a.approver.user.first_name || ""} ${a.approver.user.last_name || ""}` 
                               : (a.approver?.user?.username || "-"))).trim()}
                        </Text>
                        <Tag color={isRejected ? "error" : isApproved ? "success" : "default"} style={{ margin: 0 }}>
                          {a.status === "Cancelled" ? "Skipped (Leave Cancelled)" : a.status}
                        </Tag>
                      </div>

                      {/* Designation */}
                      {a.approver?.designation_name && (
                        <div style={{ color: "#8c8c8c", fontSize: "13px", marginTop: "2px" }}>
                          {a.approver.designation_name}
                        </div>
                      )}

                      {/* Combined Action Details (Additive) */}
                      <div style={{ marginTop: "6px" }}>
                        {/* Regular Approval / Rejection Block */}
                        {(a.approved_on || a.rejected_on || a.approver_reason || a.rejection_reason) && (
                          <div style={{ marginBottom: "8px" }}>
                            {(a.approved_on || a.rejected_on) && (
                              <div style={{ color: "#595959", fontSize: "12px", marginBottom: "2px" }}>
                                <Text strong style={{ fontSize: "11px", color: "#595959" }}>{a.rejected_on ? "Rejected On: " : "Approved On: "}</Text>
                                {dayjs(a.approved_on || a.rejected_on).format("DD MMM YYYY [at] hh:mm A")}
                              </div>
                            )}
                            {(a.approver_reason || a.rejection_reason) && (
                              <div style={{ fontSize: "13px" }}>
                                <Text strong>{a.rejection_reason ? "Rejection Remark: " : "Remark: "}</Text>
                                <Text>{a.rejection_reason || a.approver_reason}</Text>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Cancellation Block */}
                        {(a.cancellation_approved_on || a.cancellation_rejected_on || a.cancellation_reason || a.cancellation_rejected_reason) && (
                          <div style={{ 
                            marginTop: (a.approved_on || a.rejected_on) ? "8px" : "0", 
                            paddingTop: (a.approved_on || a.rejected_on) ? "8px" : "0", 
                            borderTop: (a.approved_on || a.rejected_on) ? "1px dashed #e8e8e8" : "none" 
                          }}>
                            {(a.cancellation_approved_on || a.cancellation_rejected_on) && (
                              <div style={{ color: "#595959", fontSize: "12px", marginBottom: "2px" }}>
                                <Text strong style={{ fontSize: "11px", color: "#595959" }}>Cancellation {a.cancellation_rejected_on ? "Rejected On: " : "Approved On: "}</Text>
                                {dayjs(a.cancellation_approved_on || a.cancellation_rejected_on).format("DD MMM YYYY [at] hh:mm A")}
                              </div>
                            )}
                            {(a.cancellation_reason || a.cancellation_rejected_reason) && (
                              <div style={{ fontSize: "13px" }}>
                                <Text strong>Cancellation Remark: </Text>
                                <Text>{a.cancellation_rejected_reason || a.cancellation_reason}</Text>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ),
                };
              })}
            />
          </div>
        );
      },
    },
    {
      title: "Document",
      dataIndex: "leave_document",
      key: "leave_document",
      render: (file) =>
        file ? (
          <a href={file} target="_blank" rel="noopener noreferrer">
            View
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: "Cancel",
      dataIndex: "cancel",
      render: (_, record) =>
        record.status !== "Cancellation Approved" &&
        record.status !== "Rejected" && (
          <Button
            type="text"
            danger
            icon={<CloseOutlined />}
            onClick={() => {
              setSelectedRecord(record);
              setIsModalVisible(true);
            }}
            disabled={record.created_by_system === true}
          />
        ),
    },
  ];

  // ✅ Handle Leave Cancel
  const handleCancelLeave = async () => {
    try {
      await axios.post(
        `${backUrl}/api/hr/leaves/${selectedRecord.id}/cancel/`,
        { cancellation_reason: leaveCancelReason },
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );
      setIsModalVisible(false);
      setLeaveCancelReason("");
      setSelectedRecord(null);
      await fetchData();
    } catch (error) {
      console.error("Error cancelling leave:", error);
    }
  };

  // ✅ FIXED FILTER LOGIC
  const filteredData = leaves.filter((item) => {
    const start = dayjs(item.start_date);

    const matchLeaveType = filters.leaveType
      ? item.leave_type?.toLowerCase() === filters.leaveType?.toLowerCase()
      : true;
    const matchStatus = filters.status
      ? item.status === filters.status ||
      item.approvals?.some((a) => a.status === filters.status)
      : true;
    const matchMonth = filters.month ? start.month() + 1 === filters.month : true;
    const matchYear = filters.year ? start.year() === filters.year : true;
    const matchDateRange =
      filters.fromDate && filters.toDate
        ? start.isBetween(filters.fromDate, filters.toDate, "day", "[]")
        : filters.fromDate
          ? start.isSameOrAfter(filters.fromDate, "day")
          : filters.toDate
            ? start.isSameOrBefore(filters.toDate, "day")
            : true;

    return (
      matchLeaveType &&
      matchStatus &&
      matchMonth &&
      matchYear &&
      matchDateRange
    );
  });


  const years = Array.from(
    new Set(leaves.map((item) => dayjs(item.start_date).year()))
  ).sort((a, b) => b - a);

  return (
    <>
      <Modal
        title="Cancel Leave"
        open={isModalVisible}
        onOk={handleCancelLeave}
        onCancel={() => {
          setIsModalVisible(false);
          setLeaveCancelReason("");
        }}
        okText="Submit"
        cancelText="Close"
        okButtonProps={{ danger: true }}
      >
        <Input.TextArea
          rows={4}
          placeholder="Enter cancel reason"
          value={leaveCancelReason}
          onChange={(e) => setLeaveCancelReason(e.target.value)}
        />
      </Modal>

      {/* ✅ Filters */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: "10px",
          marginBottom: "16px",
          flexDirection: isMobile ? "column" : "row",
          alignItems: "center",
        }}
      >
        <Select
          placeholder="Filter by Year"
          style={{ width: isMobile ? "100%" : 140 }}
          allowClear
          onChange={(value) => setFilters({ ...filters, year: value })}
        >
          {years.map((year) => (
            <Select.Option key={year} value={year}>
              {year}
            </Select.Option>
          ))}
        </Select>

        <Select
          placeholder="Filter by Month"
          style={{ width: isMobile ? "100%" : 160 }}
          allowClear
          onChange={(value) => setFilters({ ...filters, month: value })}
        >
          {dayjs.months().map((month, index) => (
            <Select.Option key={index} value={index + 1}>
              {month}
            </Select.Option>
          ))}
        </Select>

        <DatePicker
          placeholder="From Date"
          format="DD/MM/YYYY"
          style={{ width: isMobile ? "100%" : 160 }}
          onChange={(date) => setFilters({ ...filters, fromDate: date })}
        />
        <DatePicker
          placeholder="To Date"
          format="DD/MM/YYYY"
          style={{ width: isMobile ? "100%" : 160 }}
          onChange={(date) => setFilters({ ...filters, toDate: date })}
        />

        <Select
          placeholder="Leave Type"
          style={{ width: isMobile ? "100%" : 180 }}
          allowClear
          onChange={(value) => setFilters({ ...filters, leaveType: value })}
        >
          <Select.Option value="Casual">Casual</Select.Option>
          <Select.Option value="Sick">Sick</Select.Option>
          {/* <Select.Option value="Earned">Earned</Select.Option> */}
          <Select.Option value="Compoff">Weekoff</Select.Option>
          <Select.Option value="Lop">NLB (Nil Leave Balance)</Select.Option>
        </Select>

        <Select
          placeholder="Status"
          style={{ width: isMobile ? "100%" : 200 }}
          allowClear
          onChange={(value) => setFilters({ ...filters, status: value })}
        >
          {Object.keys(statusColors).map((status) => (
            <Select.Option key={status} value={status}>
              {status}
            </Select.Option>
          ))}
          {/* <Select.Option value="Cancelled">
            Cancelled
          </Select.Option> */}
        </Select>

      </div>

      {/* ✅ Table */}
      <Table
        columns={columns}
        dataSource={filteredData.map((item, index) => ({
          key: index,
          ...item,
        }))}
        pagination={{ pageSize: 5 }}
        scroll={{ x: true }}
        rowClassName={(record) => {
          const targetIdNum = parseInt(targetLeaveId, 10);
          return record.id === targetIdNum ? "highlighted-row" : "";
        }}
        onRow={(record) => {
          const targetIdNum = parseInt(targetLeaveId, 10);
          return {
            ref: record.id === targetIdNum ? highlightRowRef : null,
          };
        }}
        components={{
          header: {
            cell: (props) => (
              <th
                {...props}
                style={{
                  backgroundColor: "#0e497b",
                  color: "white",
                  whiteSpace: "nowrap",
                }}
              >
                {props.children}
              </th>
            ),
          },
        }}
        bordered
      />
    </>
  );
}
